import React from 'react';

import BenefitItem from './PassBenefitItem';
import PassHeader from './PassHeader';

export interface PassCardProps {
    title: string;
    dateRange?: string;
    startDate?: string | null;
    endDate?: string | null;
    price: string | number;
    originalPrice?: string | null;
    description?: string;
    benefits?: string[];
    isActive?: boolean;
    isEnabled?: boolean;
    onClick?: (data: unknown, e: React.MouseEvent<HTMLDivElement>) => void;
    isEarlyBird?: boolean;
    discountText?: string | null;
    discountName?: string | null;
    savedAmount?: string | null;
    whoForLabel?: string;
    accessLabel?: string;
    passData?: unknown;
    isPopular?: boolean;
    isFirst?: boolean;
}

const PassCard: React.FC<PassCardProps> = ({
    title,
    dateRange,
    startDate,
    endDate,
    price,
    originalPrice,
    benefits = [],
    isActive = false,
    isEnabled = false,
    onClick,
    isEarlyBird = false,
    discountText,
    discountName,
    savedAmount,
    accessLabel,
    passData,
    isPopular = false,
    isFirst = false,
}) => {
    const hasDiscount = Boolean(savedAmount || isEarlyBird || (discountText && originalPrice));

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isEnabled) {
            onClick?.(passData, e);
        }
    };

    return (
        <div
            className={[
                'w-full relative',
                isPopular && !isFirst && 'mt-[24px] md:mt-0',
                !isEnabled && 'opacity-40 pointer-events-none',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <div
                className={[
                    'w-full flex flex-row rounded-[6px] bg-white overflow-hidden',
                    'shadow-[0_4px_12px_-2px_rgba(12,17,29,0.12)]',
                    isEnabled ? 'cursor-pointer' : 'cursor-default',
                ].join(' ')}
                onClick={handleClick}
            >
                <div className="flex-1 flex flex-col relative">
                    <div className="p-6 flex flex-col gap-[20px] md:gap-[24px]">
                        <PassHeader
                            title={title}
                            dateRange={dateRange}
                            startDate={startDate}
                            endDate={endDate}
                            price={price}
                            originalPrice={originalPrice}
                            isActive={isActive}
                        />

                        <div className="flex flex-col items-start w-full gap-3">
                            <h4 className="font-['Noto_Sans'] text-[14px] font-medium leading-[24px] text-[#002B66] m-0 tracking-[0px]">
                                {accessLabel ?? 'Get full access to:'}
                            </h4>
                            <div className="flex flex-col md:flex-row md:flex-wrap gap-1 md:gap-x-3 md:gap-y-1 w-full items-start content-start">
                                {benefits.map((benefit, index) => (
                                    <BenefitItem key={index} text={benefit} />
                                ))}
                            </div>
                        </div>
                    </div>

                    {hasDiscount && (
                        <div className="flex w-full items-center p-[24px] bg-[#F9FAFB] mt-auto">
                            <div className="flex items-center">
                                <div className="flex items-center bg-white rounded-full px-3 py-[3px] mr-[-8px]">
                                    <span className="font-['Noto_Sans'] text-[14px] font-normal leading-[20px] text-[#475467] whitespace-nowrap">
                                        {discountName ?? (isEarlyBird ? 'Early Bird' : discountText)}
                                    </span>
                                </div>

                                {savedAmount && (
                                    <div className="flex items-center bg-white rounded-full px-3 py-[3px]">
                                        <span className="font-['Noto_Sans'] text-[14px] font-medium leading-[20px] text-[#002B66] whitespace-nowrap">
                                            {savedAmount}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {isPopular && (
                <>
                    <div className="hidden md:flex absolute left-full top-[24px] flex-col items-center justify-center bg-[#1A6FE5] rounded-r-[6px] w-[22px] h-fit py-[12px] px-[2px] gap-[8px] z-[1]">
                        <span className="text-white font-['Noto_Sans'] text-[12px] font-medium leading-[18px] uppercase -rotate-90 -translate-x-[1px] whitespace-nowrap flex items-center justify-center h-[55px]">
                            Popular
                        </span>
                    </div>

                    <div className="flex md:hidden absolute bottom-full right-[24px] items-center justify-center bg-[#1A6FE5] rounded-t-[6px] px-[12px] py-[2px] z-[1]">
                        <span className="text-white font-['Noto_Sans'] text-[12px] translate-y-[1px] font-medium leading-[18px] uppercase whitespace-nowrap">
                            Popular
                        </span>
                    </div>
                </>
            )}
        </div>
    );
};

export default PassCard;
